import React from "react";
import { CalendarIcon, LocationMarkerIcon } from "@heroicons/react/outline";
import { splitDate } from "lib/formatDate";
import Link from "next/link";
import { useRouter } from "next/router";
import Body from "components/Body";
import Image from "next/image";
import useTranslation from "next-translate/useTranslation";
import MoreLink from "components/MoreLink";

export default function NodeEventTeaser({ node, withSummary = false }) {
  const { t } = useTranslation("common");
  const router = useRouter();
  const date = splitDate(node.field_date_start);
  const dateEnd = node.field_date_end ? splitDate(node.field_date_end) : null;

  return (
    <article
      className="group flex flex-col cursor-pointer shadow-spread"
      onClick={() => router.push(node.path.alias)}
    >
      <div className="flex flex-col order-2 relative p-8 pb-16 lg:p-16 bg-white z-10">
        <h2 className="text-2xl font-important font-bold mb-8">
          <Link href={node.path.alias} passHref>
            <a>{node.title}</a>
          </Link>
        </h2>

        {node.body && withSummary && (
          <div className="prose max-w-none text-sm line-clamp-3 mb-8">
            <Body
              value={
                node.body.summary
                  ? node.body.summary
                  : node.body.value.replace(/(<([^>]+)>)/gi, "")
              }
            />
          </div>
        )}

        {date && (
          <div className="flex items-center">
            <CalendarIcon className="inline-block text-tertiary h-4 w-4 mr-4" />
            <div className="font-important font-medium text-xs tracking-wider uppercase">
              {date.day} {date.month}
              {dateEnd &&
                JSON.stringify(date) !== JSON.stringify(dateEnd) && (
                  <>
                    {" "}
                    &#8594; {dateEnd.day} {dateEnd.month}
                  </>
                )}{" "}
              {dateEnd ? dateEnd.year : date.year}
            </div>
          </div>
        )}

        {node.field_location && (
          <div className="flex items-center mt-4">
            <LocationMarkerIcon className="inline-block text-tertiary h-4 w-4 mr-4" />
            <span className="font-important font-medium text-xs tracking-wider uppercase">
              {node.field_location}
            </span>
          </div>
        )}

        <MoreLink link={node.path.alias} variant="tertiary" />
      </div>
      <div className="order-1">
        <div className="aspect-w-16 aspect-h-10 h-full overflow-hidden">
          {node.field_image?.image_style_uri.medium_16by10 && (
            <Image
              src={node.field_image.image_style_uri.medium_16by10}
              {...node.field_image.resourceIdObjMeta}
              layout="fill"
              objectFit="cover"
              className="group-hover:scale-110 transition"
            />
          )}
        </div>
      </div>
    </article>
  );
}
